import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const MONTHS_AR = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];

const ITEM_TYPES = {
  1: { ar: 'دجاج مبرد',    key: 'type1', color: '#047857' },
  3: { ar: 'دجاج مجمد',    key: 'type3', color: '#1d4ed8' },
  4: { ar: 'مقطعات مبرد',  key: 'type4', color: '#b45309' },
  5: { ar: 'دجاج مبرد GB', key: 'type5', color: '#7c3aed' },
};

function getCurrentPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function fmt(n) { return parseFloat(n || 0).toLocaleString('ar', { maximumFractionDigits: 0 }); }

export default function DashboardPage() {
  const { user } = useAuth();
  const toast    = useToast();
  const [period, setPeriod]   = useState(getCurrentPeriod());
  const [data, setData]       = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [result, payoutRow] = await Promise.all([
        api.get(`/commission/${user.id}/${period}`).catch(() => null),
        api.get(`/payouts/${['salesman','supervisor','area_supervisor'].includes(user.role) ? user.role : 'salesman'}`).catch(() => null),
      ]);
      const [targets, actuals, goal] = await Promise.all([
        api.get(`/targets/${user.id}/${period}`).catch(() => null),
        api.get(`/netsuite/actuals/${user.id}/${period}`).catch(() => null),
        api.get(`/goals/${user.id}/${period}`).catch(() => null),
      ]);
      setData({ result, targets, actuals, goal, payout: payoutRow });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [period, user]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const now = new Date();
    const promises = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const p = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      promises.push(
        api.get(`/commission/${user.id}/${p}`).catch(() => null).then(r => ({
          label: MONTHS_AR[d.getMonth()].substring(0, 3),
          'عمولة': r ? parseFloat(r.total_earned || 0) : 0,
        }))
      );
    }
    Promise.all(promises).then(res => setHistory(res.filter(r => r['عمولة'] > 0)));
  }, [user]);

  async function handleSync() {
    setSyncing(true);
    try {
      // Use self-sync endpoint (salesman-accessible, recalculates their own commission)
      await api.post('/netsuite/sync/self', { period });
      toast.success('تم تحديث البيانات واحتساب العمولة');
      await loadData();
    } catch (err) {
      toast.error(err.message || 'خطأ في المزامنة');
    } finally {
      setSyncing(false);
    }
  }

  const periods = [];
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    periods.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }

  const { result, targets, actuals, goal, payout } = data || {};

  return (
    <div className="page">
      <Header
        title="لوحة الأداء"
        rightAction={
          <button
            style={styles.syncBtn}
            onClick={handleSync}
            disabled={syncing}
            title="تحديث البيانات"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
              style={{ animation: syncing ? 'spin 0.7s linear infinite' : 'none' }}>
              <polyline points="1 4 1 10 7 10"/>
              <path d="M3.51 15a9 9 0 1 0 .49-3.51"/>
            </svg>
          </button>
        }
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div className="page-content">

        {/* Period Selector */}
        <div style={styles.periodScroll}>
          {periods.map(p => (
            <button key={p} style={{
              ...styles.periodBtn,
              background: p === period ? 'var(--primary)' : 'white',
              color:      p === period ? 'white' : 'var(--gray-600)',
              border:     `1.5px solid ${p === period ? 'var(--primary)' : 'var(--gray-200)'}`,
            }} onClick={() => setPeriod(p)}>
              {MONTHS_AR[parseInt(p.split('-')[1]) - 1]}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="loading-page"><div className="spinner" /><span>جاري التحميل...</span></div>
        ) : (
          <>
            {/* Total Commission Card */}
            <div style={styles.totalCard}>
              <div style={styles.totalBg} />
              <div style={{ position: 'relative', zIndex: 1 }}>
                <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 8 }}>إجمالي العمولة المستحقة</p>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 12 }}>
                  <span style={{ fontSize: 42, fontWeight: 800, color: 'white', lineHeight: 1 }}>
                    {result ? fmt(result.total_earned) : '0'}
                  </span>
                  <span style={{ fontSize: 18, color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>ر.س</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
                    {MONTHS_AR[parseInt(period.split('-')[1]) - 1]} {period.split('-')[0]}
                  </span>
                  {result && (
                    <span style={{ padding: '2px 10px', borderRadius: 20, color: 'white', fontSize: 12, fontWeight: 600, background: 'rgba(255,255,255,0.2)' }}>
                      {result.status === 'paid' ? '✓ مدفوع' : result.status === 'locked' ? '🔒 مقفل' : '● جاري'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* No targets warning */}
            {!targets && (
              <div style={styles.warningBox}>
                <span>⚠️</span>
                <div>
                  <p style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>لم يتم تحديد أهداف هذا الشهر بعد</p>
                  <p style={{ fontSize: 12, color: 'var(--gray-500)', margin: '2px 0 0' }}>تواصل مع مديرك لتحديد الأهداف</p>
                </div>
              </div>
            )}

            {/* Item Type QTY Cards */}
            <div style={styles.sectionHeader}>
              <span style={styles.sectionTitle}>الكميات حسب نوع المنتج</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              {Object.entries(ITEM_TYPES).map(([typeId, cfg]) => {
                const actualKey = `qty_${cfg.key}_actual`;
                const targetKey = `qty_${cfg.key}_target`;
                const earnedKey = `qty_${cfg.key}_earned`;
                const actual_val = parseFloat(actuals?.[actualKey] || 0);
                const target_val = parseFloat(targets?.[targetKey] || 0);
                const earned_val = parseFloat(result?.[earnedKey] || 0);
                const pct = target_val > 0 ? Math.min((actual_val / target_val) * 100, 100) : 0;
                const maxPayout = parseFloat(payout?.[`max_qty_${cfg.key}`] || 250);

                return (
                  <div key={typeId} style={{ ...styles.typeCard, borderTop: `3px solid ${cfg.color}` }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: cfg.color, marginBottom: 6 }}>{cfg.ar}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--gray-800)' }}>
                      {fmt(actual_val)}
                    </div>
                    {target_val > 0 && (
                      <>
                        <div style={{ fontSize: 10, color: 'var(--gray-400)', marginBottom: 4 }}>
                          من {fmt(target_val)} قطعة
                        </div>
                        <div className="kpi-bar">
                          <div className={`kpi-bar-fill ${pct >= 90 ? 'green' : pct >= 60 ? 'yellow' : 'red'}`}
                               style={{ width: `${pct}%` }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                          <span style={{ fontSize: 10, color: 'var(--gray-400)' }}>{pct.toFixed(1)}%</span>
                          <span style={{ fontSize: 10, fontWeight: 600, color: cfg.color }}>{fmt(earned_val)} ر.س</span>
                        </div>
                      </>
                    )}
                    {target_val === 0 && (
                      <div style={{ fontSize: 10, color: 'var(--gray-300)' }}>لا يوجد هدف</div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Other KPIs */}
            <div style={styles.sectionHeader}>
              <span style={styles.sectionTitle}>المؤشرات الأخرى</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

              {/* Bad Return */}
              <KpiRow
                label="المرتجع الرديء"
                icon="↩️"
                actual={`${parseFloat(actuals?.bad_return_actual_pct || 0).toFixed(2)}%`}
                target={`≤ ${parseFloat(targets?.bad_return_threshold || 3)}%`}
                earned={parseFloat(result?.bad_return_earned || 0)}
                maxEarned={parseFloat(payout?.max_bad_return || 375)}
                good={parseFloat(actuals?.bad_return_actual_pct || 0) <= parseFloat(targets?.bad_return_threshold || 3)}
                pct={parseFloat(actuals?.bad_return_actual_pct || 0) <= parseFloat(targets?.bad_return_threshold || 3) ? 100 : 0}
                inverted
              />

              {/* Coverage */}
              <KpiRow
                label="التغطية"
                icon="🗺️"
                actual={`${parseInt(actuals?.coverage_actual || 0)} عميل`}
                target={`${parseInt(targets?.coverage_target || 0)} عميل`}
                earned={parseFloat(result?.coverage_earned || 0)}
                maxEarned={parseFloat(payout?.max_coverage || 375)}
                pct={targets?.coverage_target > 0 ? Math.min((parseInt(actuals?.coverage_actual || 0) / parseInt(targets?.coverage_target)) * 100, 100) : 0}
              />

              {/* Collection */}
              <KpiRow
                label="التحصيل"
                icon="💰"
                actual={(() => {
                  const pct = parseFloat(actuals?.collection_actual_pct || 0);
                  const tgt = parseFloat(targets?.collection_target_amount || 0);
                  const amt = tgt > 0 ? Math.max(0, (pct / 100) * tgt) : 0;
                  return tgt > 0 ? `${fmt(amt)} ر.س (${pct.toFixed(1)}%)` : `${pct.toFixed(1)}%`;
                })()}
                target={`${fmt(targets?.collection_target_amount || 0)} ر.س`}
                earned={parseFloat(result?.collection_earned || 0)}
                maxEarned={parseFloat(payout?.max_collection || 375)}
                pct={parseFloat(actuals?.collection_actual_pct || 0)}
                note={actuals?.collection_refund_amount > 0 ? `بعد خصم المرتجعات: ${fmt(actuals.collection_refund_amount)} ر.س` : null}
              />

              {/* Specific Goal */}
              <div style={styles.kpiCard}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 18 }}>⭐</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gray-700)' }}>الهدف الخاص</div>
                      {goal?.description && (
                        <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2 }}>{goal.description}</div>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--primary)' }}>
                      {fmt(result?.specific_earned || 0)} ر.س
                    </div>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
                      background: goal?.status === 'achieved' ? 'var(--green-100)' : goal?.status === 'not_achieved' ? '#fee2e2' : 'var(--gray-100)',
                      color: goal?.status === 'achieved' ? 'var(--green-700)' : goal?.status === 'not_achieved' ? '#991b1b' : 'var(--gray-500)',
                    }}>
                      {goal?.status === 'achieved' ? '✓ محقق' : goal?.status === 'not_achieved' ? '✗ غير محقق' : '⏳ قيد التقييم'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Trend Chart */}
            {history.length > 1 && (
              <div style={{ background: 'white', borderRadius: 16, padding: 16, marginTop: 16, boxShadow: 'var(--shadow-sm)', border: '1px solid var(--gray-100)' }}>
                <p style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>اتجاه العمولات</p>
                <ResponsiveContainer width="100%" height={130}>
                  <LineChart data={history} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={v => v.toLocaleString('ar') + ' ر.س'} />
                    <Line type="monotone" dataKey="عمولة" stroke="var(--primary)" strokeWidth={2.5}
                      dot={{ fill: 'var(--primary)', r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function KpiRow({ label, icon, actual, target, earned, maxEarned, pct, inverted, note }) {
  const barColor = inverted
    ? (pct === 100 ? 'green' : 'red')
    : (pct >= 90 ? 'green' : pct >= 60 ? 'yellow' : 'red');

  return (
    <div style={styles.kpiCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 16 }}>{icon}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--gray-700)' }}>{label}</span>
        </div>
        <div style={{ textAlign: 'left' }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--primary)' }}>{(earned||0).toLocaleString('ar', {maximumFractionDigits:0})}</span>
          <span style={{ fontSize: 11, color: 'var(--gray-400)' }}> / {(maxEarned||0).toLocaleString('ar', {maximumFractionDigits:0})} ر.س</span>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--gray-800)' }}>{actual}</span>
        <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>الهدف: {target}</span>
      </div>
      <div className="kpi-bar">
        <div className={`kpi-bar-fill ${barColor}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      {note && <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 4 }}>*{note}</div>}
      <div style={{ textAlign: 'left', marginTop: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: barColor === 'green' ? 'var(--green-600)' : barColor === 'yellow' ? '#d97706' : 'var(--danger)' }}>
          {pct.toFixed(1)}%
        </span>
      </div>
    </div>
  );
}

const styles = {
  periodScroll: { display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 12, marginBottom: 4, scrollbarWidth: 'none' },
  periodBtn: { padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'var(--font-main)', transition: 'all 0.2s' },
  syncBtn: { background: 'var(--gray-100)', border: 'none', width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gray-600)' },
  totalCard: { borderRadius: 20, background: 'linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 60%, var(--green-500) 100%)', padding: 24, marginBottom: 16, position: 'relative', overflow: 'hidden', boxShadow: 'var(--shadow-green)' },
  totalBg: { position: 'absolute', top: -20, left: -20, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' },
  warningBox: { background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 },
  sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  sectionTitle: { fontSize: 15, fontWeight: 700, color: 'var(--gray-800)' },
  typeCard: { background: 'white', borderRadius: 12, padding: '12px 14px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--gray-100)' },
  kpiCard: { background: 'white', borderRadius: 14, padding: '14px 16px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--gray-100)' },
};
